import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const ROLE_LABELS = {
  'open access user': 'Open Access User',
  educator: 'Educator',
  moderator: 'Moderator',
  admin: 'Administrator',
};

function SideMenu({
  menuOpen,
  closeMenu,
  currentUser,
  profileName,
  role,
  setFaqOpen,
  moderationUnread = 0
}) {
  const { pathname } = useLocation();
  const isActive = (path) => pathname === path;

  const navLink = (to, icon, label, badge = 0) => (
    <li key={to}>
      <Link
        to={to}
        onClick={closeMenu}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors font-medium ${
          isActive(to)
            ? 'bg-brand-600 text-white'
            : 'text-gray-200 hover:bg-gray-800 hover:text-white'
        }`}
      >
        {icon}
        <span>{label}</span>
        {badge > 0 && (
          <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </Link>
    </li>
  );

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        } z-40`}
        onClick={closeMenu}
        aria-hidden={!menuOpen}
      />

      {/* Side menu */}
      <nav
        id="side-menu"
        className={`fixed top-0 left-0 h-full w-72 md:w-80 bg-gray-900 text-gray-100 shadow-2xl transform transition-transform z-50 overflow-y-auto flex flex-col ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!menuOpen}
      >
        {/* Drawer header */}
        <div className="relative bg-gray-900 text-white p-5 border-b border-gray-800 flex-shrink-0">
          <button
            className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 text-white rounded-full w-8 h-8 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors"
            onClick={closeMenu}
            aria-label="Close menu"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {currentUser ? (
            <div className="flex items-center gap-3 pr-8">
              <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-white font-bold text-base">
                {(profileName || currentUser?.displayName || currentUser?.email || '?').charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="font-semibold leading-tight truncate">
                  {profileName || currentUser?.displayName || currentUser?.email}
                </div>
                {role && (
                  <span className="inline-block mt-1 text-xs bg-brand-600/30 text-brand-300 px-2 py-0.5 rounded-full font-medium">
                    {ROLE_LABELS[role] || role}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="font-bold text-lg">Share2Teach</div>
              <div className="text-xs text-gray-400 mt-0.5">Menu</div>
            </div>
          )}
        </div>

        {/* Links */}
        <div className="p-4 flex-1">
          <p className="text-xs uppercase tracking-wider text-gray-500 px-3 mb-2 font-semibold">Explore</p>
          <ul className="flex flex-col gap-0.5 mb-5">
            {navLink('/', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>, 'Home')}
            {navLink('/contributors', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>, 'Contributors')}
            {navLink('/other-oers', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>, "Other OERs")}
            {navLink('/self-directed-learning', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>, 'Self-Directed Learning')}
            {navLink('/about-us', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, 'About Us')}
          </ul>

          {/* Actions */}
          {currentUser && (role === 'educator' || role === 'moderator' || role === 'admin') && (
            <>
              <p className="text-xs uppercase tracking-wider text-gray-500 px-3 mb-2 font-semibold">Actions</p>
              <ul className="flex flex-col gap-0.5 mb-5">
                {(role === 'educator' || role === 'moderator' || role === 'admin') &&
                  navLink('/contribute', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>, 'Contribute')}
                {(role === 'moderator' || role === 'admin') &&
                  navLink('/moderation', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>, 'Moderate', moderationUnread)}
                {role === 'admin' &&
                  navLink('/role-assign', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>, 'Assign Roles')}
                {role === 'admin' &&
                  navLink('/moderator-assignments', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>, 'Moderator Assignments')}
              </ul>
            </>
          )}

          {/* Account */}
          {currentUser ? (
            <>
              <p className="text-xs uppercase tracking-wider text-gray-500 px-3 mb-2 font-semibold">Account</p>
              <ul className="flex flex-col gap-0.5 mb-5">
                {navLink('/view-account', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>, 'View Account')}
              </ul>
            </>
          ) : (
            <>
              <p className="text-xs uppercase tracking-wider text-gray-500 px-3 mb-2 font-semibold">Account</p>
              <ul className="flex flex-col gap-0.5 mb-5">
                {navLink('/sign-in', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>, 'Sign In')}
                {navLink('/sign-up', <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>, 'Sign Up')}
              </ul>
            </>
          )}

          {/* Help */}
          <p className="text-xs uppercase tracking-wider text-gray-500 px-3 mb-2 font-semibold">Help</p>
          <ul className="flex flex-col gap-0.5">
            <li>
              <button
                onClick={() => { setFaqOpen(true); closeMenu(); }}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg text-gray-200 hover:bg-gray-800 hover:text-white transition-colors font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>FAQ</span>
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
}

export default SideMenu;
